import { DefaultRootState } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import {
  hrSelector,
  pAmplitudeSelector,
  sinusPDurationSelector,
  minPrIntervalSelector,
  qrsAxisSelector,
  svQrsDurationSelector,
  qtcIntervalSelector,
} from '../../metric/selectors/computed-metrics.selectors';
import { manualAbnormalitiesSelector } from '../../diagnostic/selectors/user-diagnostic.selectors';
import {
  AVB1_EXCLUSION_LABEL_CODES,
  IVCD_EXCLUSION_LABEL_CODES,
} from '../../resting/resting.util';
import { getLongestRRSelector } from '../../wave/selectors/wave.selectors';
import {
  AVB1_BLOCK_PR_DUR_THRES_MS,
  BORDERLINE_QTC_INTERVAL_F_DUR_THRES_MS,
  BORDERLINE_QTC_INTERVAL_M_DUR_THRES_MS,
  BRADYCARDIA_HR_DUR_THRES_MS,
  IVCD110_QRS_DUR_THRES_MS,
  IVCD130_QRS_DUR_THRES_MS,
  LAE_DUR_THRES_MS,
  LEFT_QRS_AXIS_THRES_DEG,
  SIGNIFICANT_QTC_PROLONG_F_DUR_THRES_MS,
  SIGNIFICANT_QTC_PROLONG_M_DUR_THRES_MS,
  RAE_MLII_AMPL_THRES_MV,
  RAE_V1_V2_AMPL_THRES_MV,
  RIGHT_QRS_AXIS_THRES_DEG,
  RIGHT_SUP_QRS_AXIS_THRES_DEG,
  SEVERE_BRADYCARDIA_HR_DUR_THRES_MS,
  SHORT_PR_DUR_THRES_MS,
  SHORT_QTCB_DUR_THRES_MS,
  TACHYCARDIA_HR_DUR_THRES_MS,
  VENTRICULAR_PAUSE_RR_DUR_THRES_MS,
} from '../utils/auto-label.utils';
import { definitionsSelector } from '../../diagnostic/selectors/diagnostic.selectors';
import { sexSelector } from '../../ecg/selectors/ecg.selectors';

export const autoLabelsSelector = ({ autoLabels }: DefaultRootState) =>
  autoLabels;

export const laeRaeStatusSelector = createSelector(
  sinusPDurationSelector,
  pAmplitudeSelector,
  manualAbnormalitiesSelector,
  (pDuration, pAmplitude) => {
    const isLae = !!pDuration && pDuration >= LAE_DUR_THRES_MS;

    const isRae =
      !!pAmplitude &&
      ((!!pAmplitude['MLII'] && pAmplitude['MLII'] >= RAE_MLII_AMPL_THRES_MV) ||
        (!!pAmplitude['V1'] && pAmplitude['V1'] >= RAE_V1_V2_AMPL_THRES_MV) ||
        (!!pAmplitude['V2'] && pAmplitude['V2'] >= RAE_V1_V2_AMPL_THRES_MV));

    return {
      isLae: isLae,
      isRae: isRae,
      isLaeRae: isLae && isRae,
    };
  },
);

// CL_p_1
export const rightAtrialEnlargementSelector = createSelector(
  laeRaeStatusSelector,
  (laeRaeStatus) => {
    return laeRaeStatus.isRae && !laeRaeStatus.isLae;
  },
);

// CL_p_2
export const leftAtrialEnlargementSelector = createSelector(
  laeRaeStatusSelector,
  (laeRaeStatus) => {
    return laeRaeStatus.isLae && !laeRaeStatus.isRae;
  },
);

// CL_p_3
export const leftRightAtrialEnlargementSelector = createSelector(
  laeRaeStatusSelector,
  (laeRaeStatus) => {
    return laeRaeStatus.isLaeRae;
  },
);

// CL_path_morpho_1
export const ivcd130Selector = createSelector(
  svQrsDurationSelector,
  manualAbnormalitiesSelector,
  (qrsDuration, userAbnormalities) => {
    const sharedCodeWithExclusionList = userAbnormalities.filter(({ code }) =>
      IVCD_EXCLUSION_LABEL_CODES.includes(code),
    );

    return (
      sharedCodeWithExclusionList.length === 0 &&
      !!qrsDuration &&
      qrsDuration >= IVCD130_QRS_DUR_THRES_MS
    );
  },
);

// CL_pr_2
export const shortPrIntervalSelector = createSelector(
  minPrIntervalSelector,
  (minPrInterval) => {
    return !!minPrInterval && minPrInterval < SHORT_PR_DUR_THRES_MS;
  },
);

// CL_pr_3
export const avBlockSelector = createSelector(
  minPrIntervalSelector,
  manualAbnormalitiesSelector,
  (minPrInterval, userAbnormalities) => {
    const userAbnormalityCodes = userAbnormalities.map(({ code }) => code);
    const sharedCodeWithExclusionList = userAbnormalityCodes.filter((code) =>
      AVB1_EXCLUSION_LABEL_CODES.includes(code),
    );
    return (
      sharedCodeWithExclusionList.length === 0 &&
      !!minPrInterval &&
      minPrInterval >= AVB1_BLOCK_PR_DUR_THRES_MS
    );
  },
);

// CL_qrs_axis_2
export const leftQrsAxisDeviationSelector = createSelector(
  qrsAxisSelector,
  (qrsAxis) => {
    return (
      !!qrsAxis &&
      qrsAxis <= LEFT_QRS_AXIS_THRES_DEG.max &&
      qrsAxis > LEFT_QRS_AXIS_THRES_DEG.min
    );
  },
);

// CL_qrs_axis_3
export const rightQrsAxisDeviationSelector = createSelector(
  qrsAxisSelector,
  (qrsAxis) => {
    return (
      !!qrsAxis &&
      qrsAxis < RIGHT_QRS_AXIS_THRES_DEG.max &&
      qrsAxis >= RIGHT_QRS_AXIS_THRES_DEG.min
    );
  },
);

// CL_qrs_axis_4
export const rightSuperiorQrsAxisSelector = createSelector(
  qrsAxisSelector,
  (qrsAxis) => {
    return (
      !!qrsAxis &&
      qrsAxis <= RIGHT_SUP_QRS_AXIS_THRES_DEG.max &&
      qrsAxis >= RIGHT_SUP_QRS_AXIS_THRES_DEG.min
    );
  },
);

// CL_qrs_morpho_14
export const ivcd110Selector = createSelector(
  svQrsDurationSelector,
  manualAbnormalitiesSelector,
  (qrsDuration, userAbnormalities) => {
    const userAbnormalityCodes = userAbnormalities.map(({ code }) => code);
    const sharedCodeWithExclusionList = userAbnormalityCodes.filter((code) =>
      IVCD_EXCLUSION_LABEL_CODES.includes(code),
    );

    return (
      sharedCodeWithExclusionList.length === 0 &&
      !!qrsDuration &&
      qrsDuration >= IVCD110_QRS_DUR_THRES_MS &&
      qrsDuration < IVCD130_QRS_DUR_THRES_MS
    );
  },
);

// CL_repol_qt_1
export const shortQtcIntervalSelector = createSelector(
  qtcIntervalSelector,
  (qtcInterval) => {
    return qtcInterval != null && qtcInterval <= SHORT_QTCB_DUR_THRES_MS;
  },
);

// CL_repol_qt_3
export const qtcProlongationSelector = createSelector(
  qtcIntervalSelector,
  sexSelector,
  (qtcInterval, sex) => {
    if (!qtcInterval) {
      return false;
    }
    if (sex === 'F') {
      return qtcInterval >= SIGNIFICANT_QTC_PROLONG_F_DUR_THRES_MS;
    }
    return qtcInterval >= SIGNIFICANT_QTC_PROLONG_M_DUR_THRES_MS;
  },
);

// CL_repol_qt_4
export const borderlineQtcIntervalSelector = createSelector(
  qtcIntervalSelector,
  sexSelector,
  (qtcInterval, sex) => {
    if (!qtcInterval) {
      return false;
    }
    if (sex === 'F') {
      return (
        qtcInterval >= BORDERLINE_QTC_INTERVAL_F_DUR_THRES_MS.min &&
        qtcInterval < BORDERLINE_QTC_INTERVAL_F_DUR_THRES_MS.max
      );
    }
    return (
      qtcInterval >= BORDERLINE_QTC_INTERVAL_M_DUR_THRES_MS.min &&
      qtcInterval < BORDERLINE_QTC_INTERVAL_M_DUR_THRES_MS.max
    );
  },
);

// CL_rh_2
export const ventricularPauseSelector = createSelector(
  getLongestRRSelector,
  (RR) => {
    return !!RR && RR >= VENTRICULAR_PAUSE_RR_DUR_THRES_MS;
  },
);

const withSinusAbnormalitySelector = createSelector(
  manualAbnormalitiesSelector,
  (userAbnormalities) => {
    return userAbnormalities.some(({ code }) => code === 'CL_rh_sinus_1');
  },
);

// CL_rh_sinus_12
export const sinusBradycardiaSelector = createSelector(
  hrSelector,
  withSinusAbnormalitySelector,
  (hr, withSinusAbnormality) => {
    return (
      !!hr &&
      withSinusAbnormality &&
      hr <= BRADYCARDIA_HR_DUR_THRES_MS &&
      hr > SEVERE_BRADYCARDIA_HR_DUR_THRES_MS
    );
  },
);

// CL_rh_sinus_13
export const severeSinusBradycardiaSelector = createSelector(
  hrSelector,
  withSinusAbnormalitySelector,
  (hr, withSinusAbnormality) => {
    return (
      !!hr && withSinusAbnormality && hr <= SEVERE_BRADYCARDIA_HR_DUR_THRES_MS
    );
  },
);

// CL_rh_sinus_5
export const sinusTachycardiaSelector = createSelector(
  hrSelector,
  withSinusAbnormalitySelector,
  (hr, withSinusAbnormality) => {
    return !!hr && withSinusAbnormality && hr >= TACHYCARDIA_HR_DUR_THRES_MS;
  },
);

export const autoLabelsStatusSelector = createSelector(
  definitionsSelector,
  rightAtrialEnlargementSelector,
  leftAtrialEnlargementSelector,
  leftRightAtrialEnlargementSelector,
  ivcd130Selector,
  shortPrIntervalSelector,
  avBlockSelector,
  leftQrsAxisDeviationSelector,
  rightQrsAxisDeviationSelector,
  rightSuperiorQrsAxisSelector,
  ivcd110Selector,
  shortQtcIntervalSelector,
  qtcProlongationSelector,
  borderlineQtcIntervalSelector,
  ventricularPauseSelector,
  (
    definitions,
    rightAtrialEnlargement,
    leftAtrialEnlargement,
    leftRightAtrialEnlargement,
    ivcd130,
    shortPrInterval,
    avBlock,
    leftQrsAxisDeviation,
    rightQrsAxisDeviation,
    rightSuperiorQrsAxis,
    ivcd110,
    shortQtcInterval,
    qtcProlongation,
    borderlineQtcInterval,
    ventricularPause,
  ) => {
    const allAbnCodes = definitions.map(({ code }) => code);
    const autoLabelsStatus: Record<string, boolean> = {
      CL_p_1: rightAtrialEnlargement,
      CL_p_2: leftAtrialEnlargement,
      CL_p_3: leftRightAtrialEnlargement,
      CL_path_morpho_1: ivcd130,
      CL_pr_2: shortPrInterval,
      CL_pr_3: avBlock,
      CL_qrs_axis_2: leftQrsAxisDeviation,
      CL_qrs_axis_3: rightQrsAxisDeviation,
      CL_qrs_axis_4: rightSuperiorQrsAxis,
      CL_qrs_morpho_14: ivcd110,
      CL_repol_qt_1: shortQtcInterval,
      CL_repol_qt_3: qtcProlongation,
      CL_repol_qt_4: borderlineQtcInterval,
      CL_rh_2: ventricularPause,
    };

    return Object.keys(autoLabelsStatus).reduce((acc, code) => {
      // check if auto label is actually in the annotation grid
      if (allAbnCodes.includes(code)) {
        acc[code] = autoLabelsStatus[code];
      }
      return acc;
    }, {} as Record<string, boolean>);
  },
);

export const getAutoLabelCodesByStatusSelector = (status: boolean) =>
  createSelector(
    autoLabelsStatusSelector,
    (labels: { [key: string]: boolean }) => {
      return Object.keys(labels).filter((code) => labels[code] === status);
    },
  );

export const activeCodesSelector = createSelector(
  getAutoLabelCodesByStatusSelector(true),
  manualAbnormalitiesSelector,
  (autoLabelCodesActive, userAbnormalities) => {
    const userAbnormalityCodes = userAbnormalities.map(({ code }) => code);
    return autoLabelCodesActive.filter(
      (codeToAdd) => !userAbnormalityCodes.includes(codeToAdd),
    );
  },
);

export const inactiveCodesSelector = createSelector(
  getAutoLabelCodesByStatusSelector(false),
  manualAbnormalitiesSelector,
  (autoLabelCodesInactive, userAbnormalities) => {
    const userAbnormalityCodes = userAbnormalities.map(({ code }) => code);
    return autoLabelCodesInactive.filter((codeToRemove) =>
      userAbnormalityCodes.includes(codeToRemove),
    );
  },
);
export const codeToAddSeenListSelector = createSelector(
  autoLabelsSelector,
  (autoLabels) => autoLabels.codeToAddSeenList,
);

export const codeToRemoveSeenListSelector = createSelector(
  autoLabelsSelector,
  (autoLabels) => autoLabels.codeToRemoveSeenList,
);

export const codesToAddToAssessSelector = createSelector(
  activeCodesSelector,
  codeToAddSeenListSelector,
  (codesToAdd, codeToAddSeenList) => {
    return codesToAdd.filter((code) => !codeToAddSeenList.includes(code));
  },
);

export const codesToRemoveToAssessSelector = createSelector(
  inactiveCodesSelector,
  codeToRemoveSeenListSelector,
  (codesToRemove, codeToRemoveSeenList) => {
    return codesToRemove.filter((code) => !codeToRemoveSeenList.includes(code));
  },
);

export const hasSuggestionsToAssessSelector = createSelector(
  codesToAddToAssessSelector,
  codesToRemoveToAssessSelector,
  (codesToAddToAssess, codesToRemoveToAssess) => {
    return codesToAddToAssess.length > 0 || codesToRemoveToAssess.length > 0;
  },
);

export const assessmentStatusSelector = createSelector(
  autoLabelsSelector,
  (autoLabels) => autoLabels.status,
);

export const labelsToAddSelector = createSelector(
  autoLabelsSelector,
  (autoLabels) => autoLabels.labelsToAdd,
);

export const labelsToRemoveSelector = createSelector(
  autoLabelsSelector,
  (autoLabels) => autoLabels.labelsToRemove,
);

export const needToAssessSelector = createSelector(
  assessmentStatusSelector,
  (status) => {
    return status === 'pending';
  },
);

export const codesToAddAssessedSelector = createSelector(
  labelsToAddSelector,
  (labelsToAdd) => {
    return labelsToAdd
      .filter((label) => label.status === true)
      .map(({ code }) => code);
  },
);

export const codesToRemoveAssessedSelector = createSelector(
  labelsToRemoveSelector,
  (labelsToRemove) => {
    return labelsToRemove
      .filter((label) => label.status === true)
      .map(({ code }) => code);
  },
);

export const getLabelStatusSelector = (code: string) =>
  createSelector(
    labelsToAddSelector,
    labelsToRemoveSelector,
    (labelsToAdd, labelsToRemove) => {
      const label = labelsToAdd
        .concat(labelsToRemove)
        .find((label) => label.code === code);

      if (label) {
        return label.status;
      }
      return false;
    },
  );
